import { HEADER_BUTTON_MODE } from '@hqo/hqo-miniapp-client-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { hideNativeHeader } from 'store/sdk/actions';
import { selectIsNativeHeaderHidden } from 'store/sdk/selectors';
import { useMiniappSdk } from 'hooks/use-miniapp-sdk.hook';

/**
 * Owns the native webview header for the whole app (SMB-425):
 * - during the initial boot/loading phase the header is visible with no title and a close (X) button only;
 * - once the first page has loaded (hideNativeHeader dispatched) it stays hidden for the rest of the session;
 * - the desired state is re-asserted on route changes and on pageshow/visibilitychange, because the native
 *   shell can re-show the bar on its own (e.g. after returning from an external in-webview navigation).
 */
// eslint-disable-next-line max-lines-per-function
export const useNativeHeaderVisibility = (): void => {
  const client = useMiniappSdk();
  const dispatch = useDispatch();
  const isNativeHeaderHidden = useSelector(selectIsNativeHeaderHidden);
  const { pathname } = useLocation();
  const initialPathnameRef = useRef<string>(pathname);

  const applyHeaderState = useCallback(() => {
    if (isNativeHeaderHidden) {
      client?.header.hideHeader();
    } else {
      client?.header.setHeader('', HEADER_BUTTON_MODE.CLOSE);
      client?.header.showHeader();
    }
  }, [client, isNativeHeaderHidden]);

  useEffect(() => {
    // Any in-app navigation means the app is interactive — hide for good,
    // even on pages without an explicit ready signal.
    if (!isNativeHeaderHidden && pathname !== initialPathnameRef.current) {
      dispatch(hideNativeHeader());
      client?.header.hideHeader();

      return;
    }

    applyHeaderState();
  }, [client, dispatch, pathname, isNativeHeaderHidden, applyHeaderState]);

  useEffect(() => {
    const handlePageShow = (): void => {
      applyHeaderState();
    };
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        applyHeaderState();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [applyHeaderState]);
};
